import { create } from 'zustand';

/**
 * Черновик экрана «Новая публикация», общий для рилса и поста (02.11.2026).
 *
 * Формы рилса и поста — разные компоненты, и при смене формата одна
 * размонтируется, другая монтируется. Отмеченные аккаунты и описания держим
 * выше обеих: переключился с рилса на пост — выбор пабликов и подписи
 * остались. Медиа у форматов разное, его форма держит сама и сообщает сюда
 * только факт «файл есть» — для подтверждения смены формата и ухода со страницы.
 *
 * Черновик живёт, пока открыт экран: страница сбрасывает его при уходе.
 */
type Updater<T> = T | ((prev: T) => T);

interface PublishDraftState {
  checked: Set<string>;
  captions: Map<string, string>;
  /** В форме есть файл (видео, фото) — его потеря при смене формата болезненна. */
  hasMedia: boolean;
  /** Набор применён из «последнего выбора» — показываем «Сбросить». */
  appliedLast: boolean;
  /** Набор по умолчанию уже ставили — при смене формата второй раз не ставим. */
  initialized: boolean;
  setChecked: (next: Updater<Set<string>>) => void;
  setCaptions: (next: Updater<Map<string, string>>) => void;
  setHasMedia: (value: boolean) => void;
  setAppliedLast: (value: boolean) => void;
  setInitialized: (value: boolean) => void;
  reset: () => void;
}

const apply = <T,>(next: Updater<T>, prev: T): T =>
  typeof next === 'function' ? (next as (p: T) => T)(prev) : next;

export const usePublishDraft = create<PublishDraftState>((set) => ({
  checked: new Set(),
  captions: new Map(),
  hasMedia: false,
  appliedLast: false,
  initialized: false,
  setChecked: (next) => set((s) => ({ checked: apply(next, s.checked) })),
  setCaptions: (next) => set((s) => ({ captions: apply(next, s.captions) })),
  setHasMedia: (hasMedia) => set({ hasMedia }),
  setAppliedLast: (appliedLast) => set({ appliedLast }),
  setInitialized: (initialized) => set({ initialized }),
  reset: () => set({ checked: new Set(), captions: new Map(), hasMedia: false, appliedLast: false, initialized: false }),
}));

/** Есть ли что терять: файл, подпись или отмеченные паблики. */
export function isDraftDirty(s: { hasMedia: boolean; captions: ReadonlyMap<string, string> }): boolean {
  if (s.hasMedia) return true;
  for (const v of s.captions.values()) if (v.trim()) return true;
  return false;
}
